<template>
    <div class="doing-inventory-mobile">
        <div class="mobile-header">
            <button @click="goBack" class="back-button">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" 
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
            </button>
            <h1 class="mobile-title">{{ inventory ? inventory.reference_number : 'Inventaire' }}</h1>
            <button v-if="inventory && inventory.status === 'in_progress'" @click="showCountedList = !showCountedList" class="list-button">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" 
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="8" y1="6" x2="21" y2="6"></line>
                    <line x1="8" y1="12" x2="21" y2="12"></line>
                    <line x1="8" y1="18" x2="21" y2="18"></line>
                    <line x1="3" y1="6" x2="3.01" y2="6"></line>
                    <line x1="3" y1="12" x2="3.01" y2="12"></line>
                    <line x1="3" y1="18" x2="3.01" y2="18"></line>
                </svg>
            </button>
            <div v-else class="header-spacer"></div>
        </div>

        <div v-if="loading" class="mobile-content">
            <div class="loading-state">
                <div class="spinner"></div>
                <p>Chargement de l'inventaire...</p>
            </div>
        </div>

        <div v-else-if="!inventory" class="mobile-content">
            <div class="empty-state">
                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                    <path
                        d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z">
                    </path>
                </svg>
                <h2>Aucun inventaire trouvé</h2>
                <p>Il n'y a pas d'inventaire en cours ou en attente</p>
                <button @click="goBack" class="btn-primary">Retour au tableau de bord</button>
            </div>
        </div>

        <div v-else class="mobile-content">
            <div class="stats-section">
                <div class="stat-card">
                    <div class="stat-label">Progression</div>
                    <div class="stat-value">{{ inventory.counted_items }}/{{ inventory.total_items }}</div>
                    <div class="progress-bar">
                        <div class="progress-fill" :style="{ width: inventory.progress_percentage + '%' }"></div>
                    </div>
                </div>

                <div class="stat-row">
                    <div class="stat-small">
                        <div class="stat-small-value">{{ inventory.pending_items }}</div>
                        <div class="stat-small-label">En attente</div>
                    </div>
                    <div class="stat-small">
                        <div class="stat-small-value">{{ inventory.counted_items }}</div>
                        <div class="stat-small-label">Comptés</div>
                    </div>
                </div>

                <button v-if="inventory.status === 'draft'" @click="startInventory" class="btn-action"
                    :disabled="isProcessing">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polygon points="5 3 19 12 5 21 5 3"></polygon>
                    </svg>
                    Démarrer l'inventaire
                </button>

                <button v-else-if="inventory.status === 'in_progress' && currentItem" @click="continueInventory"
                    class="btn-action">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                    Continuer l'inventaire
                </button>

                <button v-else-if="inventory.status === 'in_progress' && !currentItem && inventory.pending_items === 0"
                    @click="completeInventory" class="btn-success" :disabled="isProcessing">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    Terminer l'inventaire
                </button>
            </div>

            <div v-if="showCountedList && inventory.status === 'in_progress'" class="counted-list-section">
                <div class="counted-list-header">
                    <h2>Produits comptés ({{ countedItems.length }})</h2>
                    <button @click="showCountedList = false" class="close-list-btn">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" 
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>

                <div v-if="countedItems.length === 0" class="empty-counted">
                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" 
                        stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M9 11l3 3L22 4"></path>
                        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                    </svg>
                    <p>Aucun produit compté pour le moment</p>
                </div>

                <div v-else class="counted-items-list">
                    <div v-for="item in countedItems" :key="item.id" 
                        @click="editCountedItem(item)" 
                        class="counted-item-card">
                        <div class="counted-item-main">
                            <div class="counted-item-name">{{ item.product?.name }}</div>
                            <div class="counted-item-counts">
                                <span class="count-expected">{{ item.expected_quantity }}</span>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" 
                                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" 
                                    stroke-linejoin="round">
                                    <polyline points="9 18 15 12 9 6"></polyline>
                                </svg>
                                <span class="count-actual">{{ item.actual_quantity }}</span>
                            </div>
                        </div>
                        <div v-if="item.difference !== 0" 
                            :class="['counted-item-diff', item.difference > 0 ? 'positive' : 'negative']">
                            {{ item.difference > 0 ? '+' : '' }}{{ item.difference }}
                        </div>
                        <div v-else class="counted-item-ok">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" 
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" 
                                stroke-linejoin="round">
                                <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="!showCountedList && currentItem && inventory.status === 'in_progress'" class="product-section">
                <div class="progress-indicator">
                    <template v-if="editingItem">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" 
                            fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" 
                            stroke-linejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                        <span style="font-size: 18px;">Modification</span>
                    </template>
                    <template v-else>
                        <span class="current-number">{{ currentItemIndex + 1 }}</span>
                        <span class="separator">/</span>
                        <span class="total-number">{{items.filter(i => i.status === 'pending').length}}</span>
                    </template>
                </div>

                <div class="product-card">
                    <div class="product-name-large">{{ currentItem.product?.name }}</div>

                    <!-- <div class="expected-quantity-display">
                        <div class="expected-label">Stock théorique</div>
                        <div class="expected-value">{{ currentItem.expected_quantity }} {{ currentItem.product?.unit ||
                            'unités' }}</div>
                    </div> -->

                    <div class="count-section">
                        <div class="count-input-container">
                            <button @click="decrementCount" class="count-btn-large minus" type="button">
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                            </button>
                            <div class="count-display">
                                <input v-model.number="actualCount" type="number" min="0" step="1" inputmode="numeric"
                                    class="count-input-large" placeholder="0" @focus="$event.target.select()" />
                                <div class="count-unit">{{ currentItem.product?.unit || 'unités' }}</div>
                            </div>
                            <button @click="incrementCount" class="count-btn-large plus" type="button">
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                            </button>
                        </div>

                        <div v-if="actualCount !== null && actualCount !== currentItem.expected_quantity"
                            :class="['difference-badge', actualCount > currentItem.expected_quantity ? 'surplus' : 'deficit']">
                            {{ actualCount > currentItem.expected_quantity ? '+' : '' }}{{ actualCount -
                            currentItem.expected_quantity }}
                        </div>
                    </div>

                    <div class="action-buttons-large">
                        <button @click="saveAndNext" class="btn-validate"
                            :disabled="isProcessing">
                            <span v-if="isProcessing">Enregistrement...</span>
                            <span v-else-if="editingItem">✓ Enregistrer</span>
                            <span v-else>{{ isLastItem ? '✓ Valider' : 'Suivant →' }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import physicalInventoryService from '@/services/physicalInventoryService';
import { useToastStore } from '@/store/modules/toastStore';

export default {
    name: 'DoingInventory',
    setup() {
        const toastStore = useToastStore();
        return { toastStore };
    },
    data() {
        return {
            loading: false,
            isProcessing: false,
            inventory: null,
            items: [],
            currentItemIndex: 0,
            actualCount: 0,
            showCountedList: false,
            editingItem: null
        }
    },
    computed: {
        currentItem() {
            if (this.editingItem) {
                return this.editingItem;
            }

            if (!this.items || this.items.length === 0) return null;

            const pendingItems = this.items.filter(item => item.status === 'pending');
            if (pendingItems.length === 0) return null;

            return pendingItems[this.currentItemIndex] || null;
        },
        isLastItem() {
            const pendingItems = this.items.filter(item => item.status === 'pending');
            return this.currentItemIndex === pendingItems.length - 1;
        },
        countedItems() {
            if (!this.items || this.items.length === 0) return [];
            return this.items.filter(item => item.status === 'counted').sort((a, b) => {
                return new Date(b.updated_at) - new Date(a.updated_at);
            });
        }
    },
    mounted() {
        this.loadInventory();
    },
    methods: {
        async loadInventory() {
            this.loading = true;
            try {
                const response = await physicalInventoryService.getInventories({
                    status: 'in_progress,draft',
                    per_page: 1,
                    page: 1
                });

                if (response.data && response.data.length > 0) {
                    const inventoryId = response.data[0].id;
                    const detailResponse = await physicalInventoryService.getInventoryById(inventoryId);
                    this.inventory = detailResponse;
                    this.items = detailResponse.items || [];
                    
                    if (this.currentItem) {
                        this.actualCount = this.currentItem.actual_quantity !== null ? this.currentItem.actual_quantity : 0;
                    }
                }
            } catch (error) {
                console.error('Erreur lors du chargement de l\'inventaire:', error);
                this.toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors du chargement de l\'inventaire'
                });
            } finally {
                this.loading = false;
            }
        },

        async startInventory() {
            if (!this.inventory) return;

            this.isProcessing = true;
            try {
                await physicalInventoryService.startInventory(this.inventory.id);
                await this.loadInventory();

                this.toastStore.addToast({
                    type: 'success',
                    message: 'Inventaire démarré'
                });
            } catch (error) {
                console.error('Erreur lors du démarrage:', error);
                this.toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors du démarrage de l\'inventaire'
                });
            } finally {
                this.isProcessing = false;
            }
        },

        continueInventory() {
            if (this.currentItem) {
                this.actualCount = this.currentItem.actual_quantity !== null ? this.currentItem.actual_quantity : 0;
            }
        },

        async saveAndNext() {
            if (!this.currentItem) return;

            this.isProcessing = true;
            try {
                console.clear
                console.log('saveAndNext():', this.currentItem.id, this.actualCount)
                await physicalInventoryService.recordItemCount(
                    this.inventory.id,
                    this.currentItem.id,
                    {
                        actual_quantity: this.actualCount
                    }
                );

                // this.toastStore.addToast({
                //     type: 'success',
                //     message: 'Comptage enregistré'
                // });

                await this.loadInventory();

                if (this.editingItem) {
                    this.editingItem = null;
                    this.actualCount = 0;
                } else if (!this.isLastItem) {
                    this.currentItemIndex++;
                    if (this.currentItem) {
                        this.actualCount = this.currentItem.actual_quantity !== null ? this.currentItem.actual_quantity : 0;
                    }
                } else {
                    this.currentItemIndex = 0;
                    this.actualCount = 0;
                }
            } catch (error) {
                console.error('Erreur lors de l\'enregistrement:', error);
                this.toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors de l\'enregistrement du comptage'
                });
            } finally {
                this.isProcessing = false;
            }
        },

        incrementCount() {
            this.actualCount++;
        },

        decrementCount() {
            if (this.actualCount > 0) {
                this.actualCount--;
            }
        },

        async completeInventory() {
            if (!confirm('Êtes-vous sûr de vouloir terminer cet inventaire ? Vous ne pourrez plus modifier les comptages.')) {
                return;
            }

            this.isProcessing = true;
            try {
                await physicalInventoryService.completeInventory(this.inventory.id);

                this.toastStore.addToast({
                    type: 'success',
                    message: 'Inventaire terminé avec succès'
                });

                this.$router.push({ name: 'dashboard' });
            } catch (error) {
                console.error('Erreur lors de la finalisation:', error);
                this.toastStore.addToast({
                    type: 'error',
                    message: error.response?.data?.message || 'Erreur lors de la finalisation'
                });
            } finally {
                this.isProcessing = false;
            }
        },

        goBack() {
            if (confirm('Êtes-vous sûr de vouloir quitter ? Vos modifications non enregistrées seront perdues.')) {
                this.$router.push({ name: 'dashboard' });
            }
        },

        editCountedItem(item) {
            this.editingItem = item;
            this.actualCount = item.actual_quantity;
            this.showCountedList = false;
        }
    }
}
</script>

<style scoped>
* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

.doing-inventory-mobile {
    min-height: 100vh;
    background: #f9fafb;
    display: flex;
    flex-direction: column;
}

.mobile-header {
    background: white;
    padding: 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    position: sticky;
    top: 0;
    z-index: 100;
}

.back-button {
    width: 40px;
    height: 40px;
    border: none;
    background: #f3f4f6;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    color: #374151;
}

.back-button:hover {
    background: #e5e7eb;
}

.back-button:active {
    transform: scale(0.95);
}

.mobile-title {
    font-size: 18px;
    font-weight: 600;
    color: #1a202c;
    flex: 1;
    text-align: center;
}

.header-spacer {
    width: 40px;
}

.list-button {
    width: 40px;
    height: 40px;
    border: none;
    background: #3FDA4D;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    color: white;
    position: relative;
}

.list-button:hover {
    background: #35c441;
}

.list-button:active {
    transform: scale(0.95);
}

.mobile-content {
    flex: 1;
    padding: 16px;
    overflow-y: auto;
    padding-bottom: 80px;
}

.loading-state,
.empty-state {
    background: white;
    padding: 60px 20px;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
}

.spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #e2e8f0;
    border-top-color: #3FDA4D;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.empty-state svg {
    color: #9ca3af;
    margin-bottom: 8px;
}

.empty-state h2 {
    font-size: 20px;
    font-weight: 600;
    color: #1a202c;
}

.empty-state p {
    color: #6b7280;
    font-size: 14px;
    max-width: 300px;
}

.empty-state .btn-primary {
    padding: 12px 24px;
    background: #3FDA4D;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.empty-state .btn-primary:hover {
    background: #35c441;
}

.stats-section {
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    margin-bottom: 16px;
}

.stat-card {
    margin-bottom: 20px;
}

.stat-label {
    font-size: 13px;
    color: #6b7280;
    margin-bottom: 8px;
}

.stat-value {
    font-size: 28px;
    font-weight: 700;
    color: #1a202c;
    margin-bottom: 12px;
}

.progress-bar {
    height: 8px;
    background: #e5e7eb;
    border-radius: 4px;
    overflow: hidden;
}

.progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #3FDA4D 0%, #35c441 100%);
    transition: width 0.3s ease;
}

.stat-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 20px;
}

.stat-small {
    background: #f9fafb;
    padding: 16px;
    border-radius: 10px;
    text-align: center;
}

.stat-small-value {
    font-size: 24px;
    font-weight: 700;
    color: #1a202c;
    margin-bottom: 4px;
}

.stat-small-label {
    font-size: 12px;
    color: #6b7280;
}

.btn-action,
.btn-success {
    width: 100%;
    padding: 14px;
    border: none;
    border-radius: 10px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.2s;
}

.btn-action {
    background: #3FDA4D;
    color: white;
}

.btn-action:hover:not(:disabled) {
    background: #35c441;
}

.btn-success {
    background: #3FDA4D;
    color: white;
}

.btn-success:hover:not(:disabled) {
    background: #35c441;
}

.btn-action:disabled,
.btn-success:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.counted-list-section {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    margin-top: 16px;
    overflow: hidden;
}

.counted-list-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    background: linear-gradient(135deg, #0A5C6B 0%, #084651 100%);
    color: white;
}

.counted-list-header h2 {
    font-size: 18px;
    font-weight: 600;
}

.close-list-btn {
    width: 36px;
    height: 36px;
    border: none;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    color: white;
}

.close-list-btn:hover {
    background: rgba(255, 255, 255, 0.3);
}

.close-list-btn:active {
    transform: scale(0.95);
}

.empty-counted {
    text-align: center;
    padding: 60px 20px;
    color: #9ca3af;
}

.empty-counted svg {
    margin-bottom: 16px;
}

.empty-counted p {
    font-size: 14px;
    color: #6b7280;
}

.counted-items-list {
    padding: 12px;
    max-height: 60vh;
    overflow-y: auto;
}

.counted-item-card {
    background: #f9fafb;
    border: 2px solid #e5e7eb;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 12px;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 12px;
}

.counted-item-card:hover {
    background: white;
    border-color: #3FDA4D;
    transform: translateX(4px);
    box-shadow: 0 2px 8px rgba(63, 218, 77, 0.15);
}

.counted-item-card:active {
    transform: translateX(2px);
}

.counted-item-main {
    flex: 1;
}

.counted-item-name {
    font-size: 15px;
    font-weight: 600;
    color: #1a202c;
    margin-bottom: 8px;
}

.counted-item-counts {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
}

.count-expected {
    color: #6b7280;
    font-weight: 500;
}

.count-expected::before {
    content: 'Th: ';
    font-size: 12px;
}

.count-actual {
    color: #1a202c;
    font-weight: 700;
    font-size: 16px;
}

.count-actual::before {
    content: 'Reel: ';
    font-size: 12px;
    font-weight: 500;
}

.counted-item-counts svg {
    color: #9ca3af;
}

.counted-item-diff {
    padding: 6px 12px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 700;
    min-width: 50px;
    text-align: center;
}

.counted-item-diff.positive {
    background: #dcfce7;
    color: #166534;
}

.counted-item-diff.negative {
    background: #fee2e2;
    color: #991b1b;
}

.counted-item-ok {
    width: 32px;
    height: 32px;
    background: #dcfce7;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #3FDA4D;
}

.product-section {
    margin-top: 20px;
}

.progress-indicator {
    text-align: center;
    font-size: 16px;
    font-weight: 700;
    color: white;
    padding: 16px;
    background: linear-gradient(135deg, #0A5C6B 0%, #084651 100%);
    border-radius: 16px 16px 0 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.current-number {
    font-size: 32px;
}

.separator {
    font-size: 24px;
    opacity: 0.7;
}

.total-number {
    font-size: 24px;
    opacity: 0.9;
}

.product-card {
    background: white;
    border-radius: 0 0 16px 16px;
    padding: 28px 20px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.product-name-large {
    font-size: 22px;
    font-weight: 700;
    color: #1a202c;
    text-align: center;
    margin-bottom: 24px;
    line-height: 1.3;
    min-height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.expected-quantity-display {
    background: #f0f9ff;
    border: 2px solid #bae6fd;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 32px;
    text-align: center;
}

.expected-label {
    font-size: 13px;
    color: #0369a1;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 8px;
}

.expected-value {
    font-size: 28px;
    font-weight: 700;
    color: #0c4a6e;
}

.count-section {
    margin-bottom: 24px;
}

.count-input-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    margin-bottom: 20px;
}

.count-btn-large {
    width: 70px;
    height: 70px;
    border: none;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.count-btn-large.minus {
    background: linear-gradient(135deg, #f87171 0%, #dc2626 100%);
    color: white;
}

.count-btn-large.plus {
    background: linear-gradient(135deg, #3FDA4D 0%, #35c441 100%);
    color: white;
}

.count-btn-large:active {
    transform: scale(0.92);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}

.count-display {
    flex: 1;
    max-width: 180px;
    text-align: center;
}

.count-input-large {
    width: 100%;
    height: 80px;
    border: 3px solid #e5e7eb;
    border-radius: 16px;
    text-align: center;
    font-size: 48px;
    font-weight: 700;
    color: #1a202c;
    transition: all 0.2s;
    background: white;
}

.count-input-large:focus {
    outline: none;
    border-color: #3FDA4D;
    background: #f0fdf4;
    box-shadow: 0 0 0 4px rgba(63, 218, 77, 0.1);
}

.count-unit {
    font-size: 13px;
    color: #6b7280;
    margin-top: 6px;
    font-weight: 500;
}

.difference-badge {
    display: inline-block;
    padding: 12px 24px;
    border-radius: 12px;
    font-size: 20px;
    font-weight: 700;
    text-align: center;
    animation: slideIn 0.3s ease;
}

.difference-badge.surplus {
    background: #dcfce7;
    color: #166534;
}

.difference-badge.deficit {
    background: #fee2e2;
    color: #991b1b;
}

@keyframes slideIn {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.action-buttons-large {
    margin-top: 28px;
}

.btn-validate {
    width: 100%;
    padding: 18px;
    border: none;
    border-radius: 14px;
    font-size: 18px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s;
    background: linear-gradient(135deg, #3FDA4D 0%, #35c441 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(63, 218, 77, 0.3);
}

.btn-validate:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(63, 218, 77, 0.4);
}

.btn-validate:active:not(:disabled) {
    transform: translateY(0);
}

.btn-validate:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
}

@media (min-width: 769px) {
    .doing-inventory-mobile {
        max-width: 480px;
        margin: 0 auto;
        box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
    }
}
</style>
